import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface SyncStatusModalProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  data: any;
  error: any;
}

export const SyncStatusModal: React.FC<SyncStatusModalProps> = ({
  open,
  onClose,
  isLoading,
  data,
  error
}) => {
  let content = null;
  const title = 'Синхронизация профилей';

  if (isLoading) {
    content = (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2}>
        <CircularProgress />
        <Typography>Пожалуйста, подождите, идет синхронизация...</Typography>
      </Box>
    );
  } else if (error) {
    let errorMessage = 'Произошла неизвестная ошибка';
    const errorData = error.data;
    
    // Helper to extract message from object or string
    const getMessage = (d: any) => {
        if (typeof d === 'string') return d;
        if (d && typeof d === 'object' && d.message) return d.message;
        return JSON.stringify(d);
    };

    const msg = getMessage(errorData);

    if (error.status === 500) {
        if (msg.includes('RETOOL_API_KEY')) {
             errorMessage = 'Ошибка сервера: RETOOL_API_KEY не найден в .env.';
        } else if (msg.includes('RETOOL_WORKFLOW_ID')) {
             errorMessage = 'Ошибка сервера: RETOOL_WORKFLOW_ID не найден в .env.';
        } else {
             errorMessage = `Ошибка сервера (${error.status}): ${msg}`;
        }
    } else if (error.status === 401) {
        errorMessage = 'Ошибка авторизации (401). Проверьте права доступа.';
    } else if (error.status === 400) {
        errorMessage = `Неверный запрос (400): ${msg}`;
    } else {
        errorMessage = `Ошибка: ${msg}`;
    }

    content = (
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2}>
        <ErrorIcon color="error" sx={{ fontSize: 60 }} />
        <Typography variant="h6" color="error">Ошибка синхронизации</Typography>
        <Typography align="center" color="textSecondary">{errorMessage}</Typography>
      </Box>
    );
  } else if (data) {
     const responseMsg = typeof data === 'string' ? data : (data.message || JSON.stringify(data));
     
     // Determine if success or error based on message content
     // User provided success messages: "Workflow trigger successfully.", "Profiles synchronized successfully"
     // User provided error messages (200 OK): "Error: ...", "No data received..."
     
     const isError = 
        responseMsg.includes('Error:') || 
        responseMsg.includes('failed') || 
        responseMsg.includes('No data received');
        
     const isSuccess = !isError && (
        responseMsg.includes('successfully') || 
        responseMsg.includes('synchronized')
     );

     if (isSuccess) {
        content = (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2}>
                <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
                <Typography variant="h6" color="success">Успешно</Typography>
                <Typography align="center">{responseMsg}</Typography>
            </Box>
        );
     } else {
        content = (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={2}>
                <ErrorIcon color="warning" sx={{ fontSize: 60 }} />
                <Typography variant="h6" color="warning">Внимание</Typography>
                <Typography align="center">{responseMsg}</Typography>
            </Box>
        );
     }
  }

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {content || <Box p={2}><Typography>Нет данных для отображения</Typography></Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading} variant="contained" color="primary">
          Закрыть
        </Button>
      </DialogActions>
    </Dialog>
  );
};
